/**
 * Deployment check (plan/portal-final/INTERFACES.md section 8). Compares the
 * version compiled into the running bundle with the `version.json` the
 * current deployment serves, and decides when the tab moves onto the new
 * deployment:
 *
 * - not busy: the next route change becomes a full navigation, and a hidden
 *   tab reloads when it becomes visible;
 * - busy (unsaved work or uploads in flight): `onUpdateReadyChange(true)`, so
 *   the watcher shows its 'Update ready' control and the person chooses.
 *
 * Framework-free and driven entirely through `deps`, so the rules are tested
 * without a DOM. `attachVersionTriggers` wires it to the window.
 */

export type CheckReason = 'mount' | 'visible' | 'focus' | 'route' | 'interval';

export const ROUTE_CHECK_MIN_INTERVAL_MS = 60_000;
export const CHECK_INTERVAL_MS = 5 * 60_000;

export interface VersionMonitorDeps {
  /** The version compiled into this bundle. */
  currentVersion: string;
  /** Resolves the deployed version, or null when it cannot be read. */
  fetchVersion: () => Promise<string | null>;
  isBusy: () => boolean;
  isHidden: () => boolean;
  reload: () => void;
  navigate: (href: string) => void;
  onUpdateReadyChange: (ready: boolean) => void;
  /** Runs before every version fetch (the PWA asks its worker to update). */
  beforeCheck?: () => void;
  /** Awaited before any reload or navigation (the PWA waits for its new worker). */
  beforeReload?: () => Promise<void>;
  now?: () => number;
}

export interface VersionMonitor {
  check(reason: CheckReason): Promise<void>;
  /** The router moved to `href` (not called for the first render). */
  routeChanged(href: string): void;
  /** The document became visible. */
  becameVisible(): Promise<void>;
  /** The busy registry changed. */
  busyChanged(): void;
  /** The person pressed Reload on the 'Update ready' control. */
  reloadNow(): Promise<void>;
  isStale(): boolean;
}

export function createVersionMonitor(deps: VersionMonitorDeps): VersionMonitor {
  const now = deps.now ?? Date.now;
  let stale = false;
  let updateReady = false;
  let leaving = false;
  let lastCheckAt = Number.NEGATIVE_INFINITY;
  let inFlight: Promise<void> | null = null;

  const setUpdateReady = (ready: boolean) => {
    if (ready === updateReady) return;
    updateReady = ready;
    deps.onUpdateReadyChange(ready);
  };

  const leave = async (go: () => void) => {
    if (leaving) return;
    leaving = true;
    try {
      await deps.beforeReload?.();
    } catch {
      // A failed worker update still leaves the network copy to load.
    }
    go();
  };

  const settle = () => {
    if (!stale) return;
    setUpdateReady(deps.isBusy());
  };

  const runCheck = async () => {
    lastCheckAt = now();
    try {
      deps.beforeCheck?.();
    } catch {
      // The worker update is best effort; the version check still runs.
    }
    let deployed: string | null = null;
    try {
      deployed = await deps.fetchVersion();
    } catch {
      deployed = null;
    }
    if (deployed && deployed !== deps.currentVersion) stale = true;
    settle();
  };

  const check = (reason: CheckReason): Promise<void> => {
    if (leaving) return Promise.resolve();
    if (reason === 'route' && now() - lastCheckAt < ROUTE_CHECK_MIN_INTERVAL_MS) {
      return Promise.resolve();
    }
    if (!inFlight) {
      inFlight = runCheck().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };

  return {
    check,
    routeChanged(href) {
      if (stale && !deps.isBusy()) {
        void leave(() => deps.navigate(href));
        return;
      }
      void check('route');
    },
    async becameVisible() {
      if (!stale) await check('visible');
      if (stale && !deps.isBusy() && !deps.isHidden()) await leave(deps.reload);
    },
    busyChanged() {
      settle();
    },
    reloadNow() {
      return leave(deps.reload);
    },
    isStale() {
      return stale;
    },
  };
}

interface TriggerTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface TriggerEnv {
  window: TriggerTarget;
  document: TriggerTarget & { readonly visibilityState: string };
  setInterval: (handler: () => void, ms: number) => unknown;
  clearInterval: (id: unknown) => void;
}

/**
 * Checks once now, on focus, when the document becomes visible and every
 * five minutes. Returns the cleanup.
 */
export function attachVersionTriggers(monitor: VersionMonitor, env: TriggerEnv): () => void {
  const onFocus = () => void monitor.check('focus');
  const onVisibility = () => {
    if (env.document.visibilityState === 'visible') void monitor.becameVisible();
  };
  env.window.addEventListener('focus', onFocus);
  env.document.addEventListener('visibilitychange', onVisibility);
  const timer = env.setInterval(() => void monitor.check('interval'), CHECK_INTERVAL_MS);
  void monitor.check('mount');
  return () => {
    env.window.removeEventListener('focus', onFocus);
    env.document.removeEventListener('visibilitychange', onVisibility);
    env.clearInterval(timer);
  };
}
